import { LocalStorageService } from 'angular-2-local-storage';
import { InteractivityDesignDimensionsService, DesignDimensions } from './../../services/interactivity-design-dimensions.service';
import { InteractivityWidgetSelectionService } from './../../services/interactivity-widget-selection.service';
import { InteractivityWidgetService } from '../../services/interactivity-widget.service';
import { InteractivityEditorService } from '../../services/interactivity-editor.service';
import { IWidget } from '../../interfaces/IWidget';
import { ReduxService } from '../../../../../services/redux.service';
import { GridVisualComponent } from './grid-visual.component';
import { ReplaySubject, Subscription, Subject } from 'rxjs';
import { Component, ViewChild, ElementRef, Renderer2, Input, HostListener, NgZone, TemplateRef, AfterViewInit, OnDestroy } from '@angular/core';

import { WidgetType } from '../../enums/WidgetType';
import { TimelineDataActions } from '../../redux/timeline-data-actions';
import { StageWidget } from '../../classes/StageWidget';
import { IInCurrentTimeCollection } from '../../interfaces/IWidgetSelection';
import { InteractivityWidgetAnimationService } from '../../services/interactivity-widget-animation.service';
import { ContextMenuOption } from '../../../../../components/shared/bb-context-menu/bb-context-menu.component';
import { modKeyDown } from '../../../../../shared/utils/mod-key-down.util';
import { OverlayService } from '../../../../../services/overlay.service';
import { EventService } from '../../../../../services/event-service';
import { InteractivityStageService } from '../../services/interactivity-stage.service';
import { debounceTime } from 'rxjs/operators';
import { BbDragEvent } from '../../../../../directives/bb-drag.directive';

@Component({
	selector: 'stage',
	templateUrl: './stage.component.html',
	styleUrls: ['./stage.component.scss']
})

export class StageComponent implements AfterViewInit, OnDestroy {

	@ViewChild('widgetSelector', { static: true }) widgetSelector: ElementRef;
	@ViewChild('stage', { static: true }) stage: ElementRef;
	@ViewChild('canvasWrapper', { static: true }) canvasWrapper: ElementRef;
	@ViewChild('mediaWrapper', { static: true }) mediaWrapper: ElementRef;
	@ViewChild('vGrid', { static: true }) vGrid: GridVisualComponent;
	@ViewChild('designDimensionsTemplate', { static: true }) designDimensionsTemplate: TemplateRef<any>;

	@Input() gridResizeStartedSubject: Subject<null>;
	@Input() gridResizeEndedSubject: Subject<null>;

	public widgetType = WidgetType;

	designWidth: number;
	designHeight: number;
	interactOptions: any;
	interactOptionsResizable: any;
	interactOptionsResizableSubject: ReplaySubject<any>;
	mediaOffsetX = 0;
	mediaOffsetY = 0;
	snapObjectSubject: ReplaySubject<any>;
	stageHeight: number;
	stageWidth: number;
	widgets: IWidget[];

	widgetEditMode = false;
	preloadedWidgets: number;
	forceClosed = false;

	// canvas drag
	draggingCanvas = false;
	canvasDragStartPosition: any = {};
	hoveredWidgetIds: string[] = [];
	currentlyInTimeWidgetIds: string[] = [];
	contextMenuStyle: any = { display: 'none' };
	contextMenuOptions: ContextMenuOption[] = [];
	designDimensionsModal: any;
	appWidgetsById: any = {};
	snappingTargets: any[] = [];
	relativeSnapPoints = [
		{ x: 0, y: 0 },
		{ x: 0, y: 0.5 },
		{ x: 0, y: 1 },
		{ x: 0.5, y: 0 },
		{ x: 0.5, y: 0.5 },
		{ x: 0.5, y: 1 },
		{ x: 1, y: 0 },
		{ x: 1, y: 0.5 },
		{ x: 1, y: 1 }
	];

	// external drag / drop
	draggingOver = false;
	fileBeingDragged = false;

	private arrowChangeSelectionWidgets: IWidget[];

	private stageStyles: any;
	// private draggingWidget: boolean = false;
	private widgetDragStartPositions: any = {};

	private dragSelecting = false;
	private x1 = 0;
	private y1 = 0;
	private x2 = 0;
	private y2 = 0;
	private offsetX = 0;
	private offsetY = 0;

	// private gridTarget: any;
	// private gridSize: number = 50;

	private extraCanvasAreaPercentage = 0.75;

	// zoom
	private scrollZoomStep = 0.1;
	private zoomableWrapper: HTMLElement;
	private zoomableContent: HTMLElement;
	private zoomMousePosition: any = {};
	private zoomFactor: number = null;
	private duplicateOptions: any = {};
	private leaveTimeout: any;
	private mouseMoveListener: any;

	private subscriptions: Subscription = new Subscription();
	private designDimensions: DesignDimensions;

	constructor(public interactivityEditorService: InteractivityEditorService,
		private widgetSelectionService: InteractivityWidgetSelectionService,
		public interactivityWidgetService: InteractivityWidgetService,
		private interactivityWidgetAnimationService: InteractivityWidgetAnimationService,
		private interactivityDesignDimensionsService: InteractivityDesignDimensionsService,
		private interactivityStageService: InteractivityStageService,
		private renderer: Renderer2,
		private eventService: EventService,
		private ngZone: NgZone,
		private localStorageService: LocalStorageService,
		private overlayService: OverlayService,
		private reduxService: ReduxService
	) {
		this.snapObjectSubject = new ReplaySubject<any>(1);
		this.interactOptions = { restrict: '', origin: 'parent' };
		this.interactOptionsResizableSubject = new ReplaySubject<any>(1);
		this.interactOptionsResizable = { preserveAspectRatio: this.interactivityWidgetService.aspectRatioLocked };
		this.stageStyles = {};
		this.widgets = [];
		this.appWidgetsById = {};
		this.subscriptions.add(this.interactivityEditorService.widgetsSubject.subscribe((widgets: IWidget[]) => {
			this.widgets = widgets;
			this.preloadedWidgets = 0;
			this.snappingTargets = [];
			widgets.forEach((w) => {
				// populate appWidgetsById
				if (w && w.config && w.config.appid) {
					this.appWidgetsById[w.config.appid] = w;
				}
				if (w && w.config && w.config.preload && w.config.preload === 'true') {
					this.preloadedWidgets ++;
				}
			});
		}));

		this.subscriptions.add(this.interactivityWidgetService.aspectRatioLockedSubject.subscribe((locked: boolean) => {
			this.interactOptionsResizableSubject.next({ preserveAspectRatio: locked });
		}));
		this.subscriptions.add(this.eventService.documentFileDragging.subscribe((isDragging) => {
			this.fileBeingDragged = isDragging;
		}));
		this.subscriptions.add(this.interactivityStageService.stageCommandObs.subscribe((stageCommand) => {
			switch (stageCommand.method) {
				case 'zoom':
					this.zoom(stageCommand.direction);
					break;
				case 'arrow':
					this.onArrow(stageCommand.direction, stageCommand.shift);
					break;
			}
		}));
		this.subscriptions.add(this.interactivityStageService.stageCommandObs.pipe(
			debounceTime(1000)
		).subscribe((stageCommand) => {
			switch (stageCommand.method) {
				case 'arrow':
					this.onArrow(stageCommand.direction, stageCommand.shift, true);
					break;
			}
		}));
	}

	@HostListener('document:mouseup', ['$event'])
	onDocumentMouseUp(ev: MouseEvent) {
		this.draggingCanvas = false;
		this.disableMouseMove();
		this.stopWidgetSelector();  // stop drag selecting when mouseup outside stage
		setTimeout(() => {
			// when not dragged this will remove the clone
			if (this.duplicateOptions.clone) {
				this.duplicateOptions.clone.remove();
				this.duplicateOptions = {};
			}
		}, 50);
	}

	ngAfterViewInit() {
		this.zoomableWrapper = this.canvasWrapper.nativeElement;
		this.zoomableContent = this.stage.nativeElement;

		this.subscriptions.add(this.interactivityDesignDimensionsService.designDimensionsSubject.subscribe((designDimensions: DesignDimensions) => {
			this.designDimensions = designDimensions;

			if (!this.designWidth) {
				// first time
				setTimeout(() => {
					this.designDimensionsChanged(designDimensions);
				});
			} else {
				this.designDimensionsChanged(designDimensions);
			}
		}));

		this.subscriptions.add(
			this.widgetSelectionService.hoveredWidgetsSubject.subscribe((widgetSelection: { widgets: string[] }) => {
				this.hoveredWidgetIds = widgetSelection.widgets;
			})
		);

		this.subscriptions.add(
			this.widgetSelectionService.currentlyInTimeSubject.subscribe((currentlyInTimeCollection: IInCurrentTimeCollection) => {
				this.currentlyInTimeWidgetIds = currentlyInTimeCollection.widgetIds;
			})
		);

		this.offsetX = 0;
		this.offsetY = 0;

		// keep for potential future usage (multiselect drag)
		// let obj = this.stage.nativeElement;
		// if (obj.offsetParent) {
		// 	do {
		// 		this.offsetX += obj.offsetLeft;
		// 		this.offsetY += obj.offsetTop;
		// 	} while (obj = obj.offsetParent);
		// }

		// listen to mainGrid resize changes to temporarilly stop mouse events in Stage
		if (this.gridResizeStartedSubject) {
			this.subscriptions.add(this.gridResizeStartedSubject.subscribe(() => {
				this.gridResizeStarted();
			}));
		}
		if (this.gridResizeEndedSubject) {
			this.subscriptions.add(this.gridResizeEndedSubject.subscribe(() => {
				this.gridResizeEnded();
			}));
		}
	}

	set useSnapping(val: boolean) {
		this.localStorageService.set('ovp-useStageSnapping', val);
	}

	get useSnapping(): boolean {
		const lsValue = this.localStorageService.get('ovp-useStageSnapping');
		return (lsValue === null || !!lsValue); // default true
	}

	setWidgetSnapPoints(widgets: IWidget[]) {
		this.snappingTargets = [];
		widgets.forEach((widget) => {
			if (widget instanceof StageWidget && widget.visibility === 'visible' && widget.elRef && widget.elRef.nativeElement) {
				this.addElementSnappingPoints(widget.elRef.nativeElement);
			}

			if (widget && (widget as any).widgets) {
				(widget as any).widgets.forEach((subWidget) => {
					if (subWidget instanceof StageWidget && subWidget.visibility === 'visible' && subWidget.elRef && subWidget.elRef.nativeElement) {
						this.addElementSnappingPoints(subWidget.elRef.nativeElement);
					}
				});
			}
		});
		this.addElementSnappingPoints(this.mediaWrapper.nativeElement);

	}

	addElementSnappingPoints(el: HTMLElement) {
		const rect = el.getBoundingClientRect();
		const props = {
			x: el.offsetLeft,
			y: el.offsetTop,
			width: rect.width,
			height: rect.height
		};

		this.snappingTargets = this.snappingTargets.concat([
			{
				x: props.x
			}, {
				x: props.x + props.width / 2
			}, {
				x: props.x + props.width
			}, {
				y: props.y
			}, {
				y: props.y + props.height / 2
			}, {
				y: props.y + props.height
			}
		]);
	}

	openDesignDimensions() {
		this.designDimensionsModal = {
			title: 'Configure the project\'s dimensions',
			component: {
				theme: 'dark-theme',
				name: 'custom-content',
				templateRef: this.designDimensionsTemplate
			}
		};
		this.overlayService.createModal(this.designDimensionsModal);
	}

	onArrow(direction: 'up' | 'right' | 'down' | 'left', shift = false, updateRedux = false) {
		const shiftMP = shift ? 10 : 1;
		const prop = direction === 'up' || direction === 'down' ? 'top' : 'left';
		const mp = (direction === 'left' || direction === 'up' ? -1 : 1) * shiftMP;
		const delta = 1 * mp;
		const animLegend = 'xpos,ypos';
		const widgets = this.arrowChangeSelectionWidgets || this.widgetSelectionService.widgetSelection.widgets;

		const widgetsChangeData: any[] = [];
		if (widgets.length) {
			widgets.forEach((widget) => {
				if (widget instanceof StageWidget
					&& widget.elRef && !widget.fromAppId && !this.interactivityWidgetService.checkForAnimations(widget.animations, animLegend)
				) {
					if (updateRedux) {
						widgetsChangeData.push({
							id: widget.id,
							config: this.evtToConf(
								{ left: parseFloat(widget.elRef.nativeElement.style.left), top: parseFloat(widget.elRef.nativeElement.style.top) }
							)
						});
					} else {
						this.renderer.setStyle(widget.elRef.nativeElement, prop, (parseFloat(widget.elRef.nativeElement.style[prop]) + delta) + 'px');
					}
				}
			});
		}

		if (this.widgetSelectionService.widgetSelection && this.widgetSelectionService.widgetSelection.widgets
			&& this.widgetSelectionService.widgetSelection.widgets.length
		) {
			this.arrowChangeSelectionWidgets = this.widgetSelectionService.widgetSelection.widgets;
		}

		if (widgets && widgetsChangeData.length) {
			this.reduxService.dispatch({
				type: TimelineDataActions.CHANGE_WIDGETS_DIFFERENT_CONFIG,
				widgets: widgetsChangeData,
				stateSet: this.interactivityEditorService.activeStateSet,
				state: this.interactivityEditorService.activeState,
				description: 'Changed position of ' + widgets.length + ' widget(s)'
			});
			this.arrowChangeSelectionWidgets = null;
		}
	}

	trackBy(ind: number, item: any) {
		return item.id || ind;
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	// listen to mainGrid resiza changes to temporarilly stop mouse events in Stage
	gridResizeStarted(): void {
		this.stageStyles['pointer-events'] = 'none';
	}
	gridResizeEnded(): void {
		this.stageStyles['pointer-events'] = 'auto';
	}

	isStageWidget(widget: IWidget): boolean {
		return (widget instanceof StageWidget);
	}

	onScroll(ev: Event) {
		// console.warn('scrollLeft', this.zoomableWrapper.scrollLeft);
		// console.warn('offsetWidth', this.zoomableWrapper.offsetWidth);
		// console.warn('scrollWidth', this.zoomableWrapper.scrollWidth);

	}

	onMouseWheel(ev: WheelEvent) {
		// console.warn('onMouseWheel', ev.deltaX, ' y ', ev.deltaY);
		ev.preventDefault();
		const maxDelta = Math.max(ev.deltaX, ev.deltaY);
		const max = maxDelta > 0 ? Math.max(this.scrollZoomStep, maxDelta) : maxDelta;
		const minDelta = Math.min(ev.deltaX, ev.deltaY);
		const min = minDelta < 0 ? Math.min(-this.scrollZoomStep, minDelta) : minDelta;
		const dir = (max > 0 ? max : min);

		if (dir > 0 && (
			this.zoomableWrapper.scrollWidth * 0.95 <= this.zoomableWrapper.offsetWidth ||
			this.zoomableWrapper.scrollHeight * 0.95 <= this.zoomableWrapper.offsetHeight
		) || (
			dir < 0 && (
				this.zoomableWrapper.scrollWidth * 0.05 >= this.zoomableWrapper.offsetWidth ||
					this.zoomableWrapper.scrollHeight * 0.05 >= this.zoomableWrapper.offsetHeight
			)
		)) {
			// don't zoom out further when canvas would get smaller then viewport
			return;
		}
		const zoomDelta = Math.max(Math.abs(ev.deltaX), Math.abs(ev.deltaY));
		const zoomMultiplier = Math.min(Math.max((zoomDelta / 150), 0.15), 1);

		const newZoomFactor = this.designDimensions.zoomFactor +
			(zoomMultiplier * this.designDimensions.zoomFactor * this.scrollZoomStep * (max > 0 ? -1 : 1));
		this.zoom(null, newZoomFactor, {
			mouseOffsetX: ev.clientX - this.zoomableWrapper.getBoundingClientRect().left,
			mouseOffsetY: ev.clientY - this.zoomableWrapper.getBoundingClientRect().top
		});
	}

	onWidgetMouseOver(widget: IWidget, ev: MouseEvent) {
		if (widget && widget.id) {
			this.widgetSelectionService.setHoveredWidgets([widget.id]);
		}
	}

	onWidgetMouseLeave() {
		this.widgetSelectionService.clearHoveredWidgets();
	}

	isWidgetHovered(widget: IWidget) {
		return (widget && widget.id && this.hoveredWidgetIds.indexOf(widget.id) !== -1);
	}

	zoom(direction: 'in' | 'out' | 'toFit', newZoomFactor?: number, zoomMousePosition?: any, fromMedia = false) {
		if (!this.zoomableWrapper) {
			return;
		}
		if (zoomMousePosition) {
			this.zoomMousePosition = zoomMousePosition;
		}

		if (direction === 'toFit') {
			const dw = (this.designWidth / this.zoomFactor);
			const dh = (this.designHeight / this.zoomFactor);
			const designAspectRatio = dw / dh;
			const stageAspectRatio = this.zoomableWrapper.offsetWidth / this.zoomableWrapper.offsetHeight;
			const widthFit = this.zoomableWrapper.offsetWidth / dw;
			const heightFit = this.zoomableWrapper.offsetHeight / dh;
			this.interactivityDesignDimensionsService.setZoomFactor(designAspectRatio > stageAspectRatio ? widthFit : heightFit, true);
			setTimeout(() => {
				this.scrollMediaInView(true);
			});
		} else if (direction === 'in') {
			this.interactivityDesignDimensionsService.zoomIn();
		} else if (direction === 'out') {
			this.interactivityDesignDimensionsService.zoomOut();
		} else if (newZoomFactor) {
			this.interactivityDesignDimensionsService.setZoomFactor(newZoomFactor);
		}
	}

	onResizeHandlerMouseDown(e: MouseEvent) {
		e.stopPropagation();
		if (e.shiftKey && !this.interactivityWidgetService.aspectRatioLocked) {	// override preserveAspectRatio (  reset in onWidgetResizeEnd()  )
			this.interactOptionsResizableSubject.next({ preserveAspectRatio: true });
		}
	}

	onStageMouseDown(e: MouseEvent): void {
		return;
		e.stopPropagation();
		this.x2 = 0;
		this.y2 = 0;

		this.dragSelecting = true;
		this.renderer.setStyle(this.widgetSelector.nativeElement, 'visibility', 'visible');

		// get parent element position in document
		this.x2 = this.x1 = e.clientX - this.offsetX; // Set the initial X
		this.y2 = this.y1 = e.clientY - this.offsetY; // Set the initial Y
		this.reCalc();
	}
	onStageMouseMove(e: MouseEvent): void {
		return;
		e.stopPropagation();
		if (!this.dragSelecting) {
			return;
		}
		this.x2 = e.clientX - this.offsetX; // Update the current position X
		this.y2 = e.clientY - this.offsetY; // Update the current position Y
		this.reCalc();
	}

	onStageMouseUp(e: MouseEvent): void {
		return;
		this.stopWidgetSelector();
	}

	onCanvasClick(ev: MouseEvent) {
		this.clearSelectedWidgets(ev);
	}

	enableMouseMove() {
		this.mouseMoveListener = this.renderer.listen( this.canvasWrapper.nativeElement, 'mousemove', (mev: MouseEvent) => {
			this.onCanvasMouseMove(mev);
		});
	}

	disableMouseMove() {
		this.mouseMoveListener?.();
	}

	onCanvasMouseDown(ev: MouseEvent) {
		if (ev && ev.button === 0 && this.zoomableWrapper) {
			this.draggingCanvas = true;
			this.enableMouseMove();
			const mouseOffsetLeft = ev.clientX - this.zoomableWrapper.getBoundingClientRect().left;
			const mouseOffsetTop = ev.clientY - this.zoomableWrapper.getBoundingClientRect().top;
			this.canvasDragStartPosition = {
				offsetWidth: this.zoomableWrapper.offsetWidth,
				offsetHeight: this.zoomableWrapper.offsetHeight,
				scrollLeft: this.zoomableWrapper.scrollLeft,
				scrollTop: this.zoomableWrapper.scrollTop,
				mouseOffsetLeft: this.zoomableWrapper.offsetWidth - mouseOffsetLeft,
				mouseOffsetTop: this.zoomableWrapper.offsetHeight - mouseOffsetTop
			};
		}
	}

	onCanvasDragMove(ev: Event) {
		console.warn('onCanvasDragMove', ev);
	}

	onCanvasMouseMove(ev: MouseEvent) {
		if (this.draggingCanvas && this.zoomableWrapper) {
			const mouseOffsetX = ev.clientX - this.zoomableWrapper.getBoundingClientRect().left;
			const mouseOffsetY = ev.clientY - this.zoomableWrapper.getBoundingClientRect().top;
			// console.warn('mouseOffset', mouseOffset, 'this.zoomableWrapper.scrollLeft', this.zoomableWrapper.scrollLeft)
			// console.warn('ev', ev.offsetX, 'mouseOffset',  mouseOffset)
			this.zoomableWrapper.scrollLeft = this.canvasDragStartPosition.scrollLeft +
				(this.zoomableWrapper.offsetWidth - mouseOffsetX) - this.canvasDragStartPosition.mouseOffsetLeft;

			this.zoomableWrapper.scrollTop = this.canvasDragStartPosition.scrollTop +
				(this.zoomableWrapper.offsetHeight - mouseOffsetY) - this.canvasDragStartPosition.mouseOffsetTop;
		}
	}


	onWidgetMouseDown(event: MouseEvent, widget: IWidget): void {
		if (event.button === 0) {
			event.stopPropagation();
			if (event && event.altKey) {
				if (this.duplicateOptions.clone) {
					this.duplicateOptions.clone.remove();
				}
				const el: any = (event.target as HTMLElement).closest('stage-widget');
				const clone = el.cloneNode(true);
				el.parentNode.insertBefore(clone, el);
				this.duplicateOptions.duplicating = true;
				this.duplicateOptions.widgetXpos = widget.getConfigValue('xpos');
				this.duplicateOptions.widgetYpos = widget.getConfigValue('ypos');
				this.duplicateOptions.mouseDownEvent = event;
				this.duplicateOptions.left = el.style.left;
				this.duplicateOptions.top = el.style.top;
				this.duplicateOptions.widget = widget;
				this.duplicateOptions.clone = clone;
			} else {
				this.selectWidget(event, widget);
			}
		}
	}

	onWidgetMouseUp(event: MouseEvent, widget: IWidget): void {
		this.stopWidgetSelector();
	}

	onWidgetClick(ev: MouseEvent, widget: IWidget): void {
		ev.stopPropagation();
		return; // placeholder
	}


	public onWidgetResizeStart(widget: IWidget, position: BbDragEvent) {
		this.setWidgetSnapPoints(this.widgets.filter(w => w.id !== widget.id || this.duplicateOptions.duplicating));
	}

	public onWidgetDragStart(widget: IWidget, position: BbDragEvent) {
		this.setWidgetSnapPoints(this.widgets.filter(w => w.id !== widget.id || this.duplicateOptions.duplicating));

		this.setWidgetPositions('start', widget, position);
	}

	public onWidgetDragging(widget: IWidget, position: BbDragEvent) {
		this.ngZone.runOutsideAngular(() => {
			this.setWidgetPositions('dragging', widget, position);
		});
	}

	public onWidgetDragEnd(widget: IWidget, position: BbDragEvent) {
		this.snappingTargets = [];
		this.setWidgetPositions('end', widget, position);
	}

	onWidgetEditModeChanged(widget: StageWidget, editing: boolean) {
		this.widgetEditMode = editing;
	}

	public onWidgetResizeEnd(widget: IWidget, evt: any) {
		const newConfig = this.evtToConf(evt, true);
		if (widget.animations.length) {
			if (
				Math.abs(parseFloat(newConfig.xpos) - parseFloat(widget.calculatedConfig.xpos)) > 0.1 ||
				Math.abs(parseFloat(newConfig.ypos) - parseFloat(widget.calculatedConfig.ypos)) > 0.1) {
				// TODO: merge into one redux action?
				const xsLegend = 'xpos,ypos';
				if (this.interactivityWidgetService.checkForAnimations(widget.animations, xsLegend)) {
					this.interactivityWidgetAnimationService.addOrUpdateKeyframe(widget.id, newConfig.xpos + ',' + newConfig.ypos, xsLegend);
				} else {
					// update normally
					const widgetsChangeData = {
						id: widget.id,
						config: newConfig
					};

					this.reduxService.dispatch({
						type: TimelineDataActions.CHANGE_WIDGETS_DIFFERENT_CONFIG,
						widgets: widgetsChangeData,
						stateSet: this.interactivityEditorService.activeStateSet,
						state: this.interactivityEditorService.activeState,
						description: 'Changed position of ' + this.widgetSelectionService.widgetSelection.widgets.length + 'widget(s)'
					});
				}
			}
			this.interactivityWidgetAnimationService.addOrUpdateKeyframe(widget.id, newConfig.width + ',' + newConfig.height, 'width,height');
		} else {
			this.reduxService.dispatch({
				type: TimelineDataActions.CHANGE_WIDGET_CONFIG,
				id: widget.id,
				config: newConfig,
				stateSet: this.interactivityEditorService.activeStateSet,
				state: this.interactivityEditorService.activeState,
				description: 'Change size of widget: ' + widget.config.name
			});
		}

		this.widgetSelectionService.clearSelectedWidgets();
		this.widgetSelectionService.addSelectedWidgetById(widget.id, { area: 'stage' });

		this.interactOptionsResizableSubject.next({ preserveAspectRatio: this.interactivityWidgetService.aspectRatioLocked });
	}

	pct2px(pctString: string, prop?: 'xpos' | 'ypos' | 'width' | 'height', fromAppWidget?: IWidget): string {
		if (typeof pctString === 'string') {
			const pct = Number(pctString.replace('%', ''));
			let ret = (pct / 100) * (prop === 'xpos' || prop === 'width' ? this.designWidth : this.designHeight);

			if (fromAppWidget) {
				ret += parseFloat(this.pct2px(fromAppWidget.config[prop] || '0%', prop));
			} else if (prop === 'xpos') {
				ret += this.mediaOffsetX;
			} else if (prop === 'ypos') {
				ret += this.mediaOffsetY;
			}
			return ret + 'px';
		}
	}

	onStageDragover(ev: Event) {
		if (!this.fileBeingDragged) {
			ev.preventDefault();
			if (this.leaveTimeout) {
				clearTimeout(this.leaveTimeout);
			}

			if (!this.draggingOver) {
				this.draggingOver = true;
			}
		}
	}

	onStageDragleave() {
		this.leaveTimeout = setTimeout(() => {
			this.draggingOver = false;
		}, 50);
	}

	filenameToLabel(filename: string) {
		if (!filename) {
			return '';
		}
		const fnArr = filename.split('.');
		fnArr.pop();
		return fnArr.join('.').replace(/_/g, ' ');
	}

	onStageDrop(ev: any) {
		ev.preventDefault();
		// const files = ev.dataTransfer.files;
		let eventData: any;
		this.draggingOver = false;
		try {
			eventData = JSON.parse(ev.dataTransfer.getData('text'));
		} catch (e) {
			return;
		}


		let newEv = this.evtToConf({ left: ev.offsetX, top: ev.offsetY });

		let changedProps: any = null;
		let widgetType;
		let widgetLabel = '';

		if (eventData && eventData.mediatype && eventData.id) {
			widgetType = eventData.mediatype;
			widgetLabel = eventData.title || (eventData.originalfilename && this.filenameToLabel(eventData.originalfilename));
			switch (eventData.mediatype) {
				case 'video':
				case 'audio':
					changedProps = {
						clipid: eventData.id
					};
					break;
				case 'image':
					changedProps = {
						imageid: eventData.id
					};
					break;
			}

			const mediaWidth = eventData.width || eventData.naturalWidth;
			const mediaHeight = eventData.height || eventData.naturalHeight;
			if (mediaWidth && mediaHeight) {
				const clipAr = this.designHeight / this.designWidth;
				changedProps.width = 15;
				changedProps.height = (changedProps.width * (mediaHeight / mediaWidth)) / clipAr;
				changedProps.width += '%';
				changedProps.height += '%';
			}
		} else if (eventData.type === 'App' && eventData.id) {
			widgetType = 'app';
			widgetLabel = eventData.name;
			changedProps = {
				appid: eventData.id
			};
			newEv = this.evtToConf({ left: this.mediaOffsetX, top: this.mediaOffsetY });
		}

		if (changedProps) {
			this.interactivityWidgetService.createWidget(widgetType, { ...newEv, ...changedProps }, null, null, widgetLabel);
		}
	}

	getOverlayStyle() {
		const style = 'px solid black';
		return {
			'border-left': this.mediaOffsetX + style,
			'border-right': this.mediaOffsetX + style,
			'border-top': this.mediaOffsetY + style,
			'border-bottom': this.mediaOffsetY + style,
			'z-index': this.widgets.length + 1
		};
	}

	contextMenuHide(): void {
		this.contextMenuStyle.display = 'none';
	}

	onGenericContextMenu(ev: MouseEvent, widget?: StageWidget) {
		if (ev) {
			ev.preventDefault();
			ev.stopPropagation();
		}

		const selectedWidgets = this.widgetSelectionService.widgetSelection.widgets;
		let selectedNonAppWidgets: any[] = [];
		// let selectedMediaWidgets: any[] = [];
		if (selectedWidgets && selectedWidgets.length) {
			// exclude widgets in apps
			selectedNonAppWidgets = selectedWidgets.filter(w => w && !w.fromAppId);
			// selectedMediaWidgets = selectedWidgets.filter(w => w && (w.type !== 'image' && w.type !== 'video'));
		}
		const mcId = widget && (
			widget.type === 'image' && widget.getConfigValue('imageid') ||
			widget.type === 'video' && widget.getConfigValue('clipid')
		);

		if (mcId) {
			this.contextMenuOptions.push({
				label: `Restore aspect ratio`,
				data: widget,
				onSelected: (option: any) => {
					if (option.data) {
						this.interactivityWidgetService.restoreAspectRatio(option.data);
					}
				},
				icon: 'crop'
			});
		}

		// Widget not in selection or only one in selection
		if (widget && (!selectedNonAppWidgets.some(w => w && w.id === widget.id) || selectedNonAppWidgets.length === 1)) {
			this.contextMenuOptions.push({
				item: widget,
				label: `Hide "${(widget.label || '')}"`,
				value: 'hide',
				icon: 'eye-slash'
			});
			if (!widget.fromAppId) {
				this.contextMenuOptions.push({
					item: widget,
					label: `Duplicate "${(widget.label || '')}"`,
					value: 'duplicate',
					icon: 'copy'
				});
				this.contextMenuOptions.push({
					item: widget,
					label: `Copy "${(widget.label || '')}"`,
					value: 'copy',
					icon: 'copy'
				});
				this.contextMenuOptions.push({
					item: widget,
					label: `Delete "${(widget.label || '')}"`,
					value: 'delete',
					icon: 'trash'
				});
			}
		} else if (widget) {
			// widget in multiselect
			this.contextMenuOptions.push({
				item: selectedNonAppWidgets,
				label: `Copy ${selectedNonAppWidgets.length} selected widgets`,
				value: 'copy_selection',
				icon: 'copy'
			});

			this.contextMenuOptions.push({
				item: selectedNonAppWidgets,
				label: `Duplicate ${selectedNonAppWidgets.length} selected widgets`,
				value: 'duplicate_selection',
				icon: 'copy'
			});

			this.contextMenuOptions.push({
				item: selectedNonAppWidgets,
				label: `Delete ${selectedNonAppWidgets.length} selected widgets`,
				value: 'delete_selection',
				icon: 'trash'
			});
		}

		if (this.interactivityWidgetService.clipBoard && this.interactivityWidgetService.clipBoard.widgets
			&& this.interactivityWidgetService.clipBoard.widgets.length
		) {
			this.contextMenuOptions.push({
				label: `Paste ${this.interactivityWidgetService.clipBoard.widgets.length} widgets`,
				value: 'paste',
				icon: 'paste'
			});
		}

		this.contextMenuShow(ev.pageX, ev.pageY);
	}

	onStageContextMenu(ev: any): void {
		this.contextMenuOptions = [];
		const hiddenWidgets = this.widgets.filter(w3 => w3 instanceof StageWidget && w3.visibility === 'hidden');
		const visibleWidgets = this.widgets.filter(w3 => w3 instanceof StageWidget && w3.visibility !== 'hidden');

		if (hiddenWidgets.length) {
			this.contextMenuOptions.push({
				label: 'SHOW_ALL_WIDGETS',
				value: 'show_all',
				icon: 'eye'
			});
		}
		if (visibleWidgets.length) {
			this.contextMenuOptions.push({
				label: 'HIDE_ALL_WIDGETS',
				value: 'hide_all',
				icon: 'eye-slash'
			});
		}

		this.contextMenuOptions.push({
			label: 'ZOOM_TO_FIT',
			value: 'zoom_to_fit',
			icon: 'search'
		});

		this.contextMenuOptions.push({
			label: this.useSnapping ? 'TURN_OFF_SNAPPING' : 'TURN_ON_SNAPPING',
			value: 'toggle_snapping',
			icon: 'magnet',
			onSelected: () => {
				this.useSnapping = !this.useSnapping;
			}
		});

		this.onGenericContextMenu(ev);
	}

	onWidgetContextMenu(ev: any, widget: any): void {
		this.contextMenuOptions = [];

		this.onGenericContextMenu(ev, widget);
	}

	onContextMenuOptionSelected(option: ContextMenuOption): void {
		const deltas = {
			xpos: ((20 / this.designWidth) * 100) + 'px',
			ypos: ((20 / this.designHeight) * 100) + 'px'
		};
		switch (option.value) {
			case 'setDesignDimensions':
				this.openDesignDimensions();
				break;

			case 'delete':
				if (option.item && option.item.id) {
					this.interactivityEditorService.removeWidget(option.item.id);
				}
				break;


			case 'delete_selection':
				if (option.item) {
					this.widgetSelectionService.deleteSelectedWidgets();
				}
				break;

			case 'hide':
			case 'show':
				const w = option.item;
				if (w instanceof StageWidget) {
					w.visibility = option.value === 'show' ? 'visible' : 'hidden';
				}
				break;

			case 'hide_all':
			case 'show_all':
				if (this.widgets && this.widgets.length) {
					this.widgets.forEach(w2 => {
						if (w2 instanceof StageWidget) {
							w2.visibility = option.value === 'show_all' ? 'visible' : 'hidden';
						}
					});
				}
				break;

			case 'zoom_to_fit':
				this.zoom('toFit');
				break;

			case 'copy':
				this.interactivityWidgetService.copy([option.item]);
				break;

			case 'copy_selection':
				this.interactivityWidgetService.copy(this.widgetSelectionService.widgetSelection.widgets);
				break;

			case 'paste':
				if (this.interactivityWidgetService.clipBoard && this.interactivityWidgetService.clipBoard.widgets
					&& this.interactivityWidgetService.clipBoard.widgets.length
				) {
					// leaving below code intact to improve on paste logic later.
					// let minXWidget: any,
					// 	minYWidget: any;
					// this.interactivityWidgetService.clipBoard.widgets.forEach((widget) => {
					// 	if (!minXWidget || (minXWidget && minXWidget.config && parseFloat(minXWidget.config.xpos) > parseFloat(widget.config.xpos))) {
					// 		minXWidget = widget;
					// 	}
					// 	if (!minYWidget || (minYWidget && minYWidget.config && parseFloat(minYWidget.config.ypos) > parseFloat(widget.config.ypos))) {
					// 		minYWidget = widget;
					// 	}
					// });
					// let cmRect = this.canvasWrapper.nativeElement.querySelector('bb-context-menu').getBoundingClientRect();
					// let stageRect = this.stage.nativeElement.getBoundingClientRect();

					// let stageX = (cmRect.left - stageRect.left);
					// let stageY = (cmRect.top - stageRect.top);

					// let minX = parseFloat(this.pct2px(minXWidget.config.xpos));
					// let minY = parseFloat(this.pct2px(minYWidget.config.ypos));

					// // let maxX = Math.max(minX,
					// // 	parseFloat(this.pct2px(minYWidget.config.xpos))
					// // );

					// // let maxY = Math.max(
					// // 	parseFloat(this.pct2px(minXWidget.config.ypos)),
					// // 	minY
					// // );
					// console.warn(minX, 'stageX', stageX, 'minXWidget', minXWidget);
					// console.warn(minY, 'stageY', stageY, 'minYWidget', minYWidget);
					// this.interactivityWidgetService.paste(this.evtToConf({
					// 	left: stageX - (minX),
					// 	top: stageY - (minY)
					// }));

					const newIds3 = this.interactivityWidgetService.duplicateWidgets(this.interactivityWidgetService.clipBoard.widgets, deltas, true);
					setTimeout(() => {
						this.widgetSelectionService.clearSelectedWidgets();
						this.widgetSelectionService.addSelectedWidgetByIdMulti(newIds3);
					}, 50);
				}
				break;

			case 'duplicate':
				const newIds = this.interactivityWidgetService.duplicateWidgets([option.item], deltas, true);
				setTimeout(() => {
					this.widgetSelectionService.clearSelectedWidgets();
					this.widgetSelectionService.addSelectedWidgetByIdMulti(newIds);
				}, 50);

				break;

			case 'duplicate_selection':
				const newIds2 = this.interactivityWidgetService.duplicateWidgets(option.item, deltas, true);
				setTimeout(() => {
					this.widgetSelectionService.clearSelectedWidgets();
					this.widgetSelectionService.addSelectedWidgetByIdMulti(newIds2);
				}, 50);
				break;
		}
		this.contextMenuHide();
	}

	closeWarning() {
		this.forceClosed = true;
	}

	private selectWidget(event: MouseEvent, widget: IWidget): void {
		event.stopPropagation();

		const modKey = modKeyDown(event);
		if (modKey) {
			if (widget.isSelected) {
				this.widgetSelectionService.removeSelectedWidget(widget);
			} else {
				this.widgetSelectionService.addSelectedWidget(widget, { area: 'stage' });
			}
		} else if (!widget.isSelected) {
			setTimeout(() => {
				// set timeout to allow the previously selected widget to be updated
				this.widgetSelectionService.clearSelectedWidgets();
				this.widgetSelectionService.addSelectedWidget(widget, { area: 'stage' });
			});
		}
	}

	private clearSelectedWidgets(event: Event): void {
		this.widgetSelectionService.clearSelectedWidgets();
	}

	private widgetDuplication(widget: IWidget, position?: any): boolean {
		if (this.duplicateOptions.duplicating) {
			const el = this.duplicateOptions.mouseDownEvent.target.closest('stage-widget');
			// revert to old position:
			el.style.left = this.duplicateOptions.left;
			el.style.top = this.duplicateOptions.top;
			if (this.duplicateOptions.clone) {
				this.duplicateOptions.clone.remove();
			}
			const newId = this.interactivityWidgetService.duplicateWidget(widget, position ? this.evtToConf({ left: position.left, top: position.top }) : null);
			setTimeout(() => {
				this.widgetSelectionService.clearSelectedWidgets();
				this.widgetSelectionService.addSelectedWidgetById(newId, { area: 'stage' });
			}, 500);
			this.duplicateOptions = {};
			return true;
		} else {
			return false;
		}
	}

	private setWidgetPositions(opt: 'start' | 'dragging' | 'end', widget: IWidget, position: BbDragEvent) {
		if (!position) {
			return;
		}
		let deltaX: number;
		let deltaY: number;
		const widgetsChangeData: any[] = [];
		if (opt === 'start') {
			this.widgetDragStartPositions[widget.id] = position;
			// this.draggingWidget = true;

		} else if (opt === 'dragging') {
			deltaX = position.left - this.widgetDragStartPositions[widget.id].left;
			deltaY = position.top - this.widgetDragStartPositions[widget.id].top;

		} else if (opt === 'end') {
			// this.draggingWidget = false;
			deltaX = position.left - this.widgetDragStartPositions[widget.id].left;
			deltaY = position.top - this.widgetDragStartPositions[widget.id].top;

			if (!this.widgetDuplication(widget, position)) {
				widgetsChangeData.push({
					id: widget.id,
					config: this.evtToConf({ left: position.left, top: position.top })
				});
			}
		}

		if (this.widgetSelectionService.widgetSelection.widgets.length === 0) {
			return;
		} else if (this.widgetSelectionService.widgetSelection.widgets.length === 1 &&
			this.widgetSelectionService.widgetSelection.widgets[0].animations.length &&
			opt === 'end'
		) {
			const d = widgetsChangeData[0];
			const animLegend = 'xpos,ypos';
			if (this.interactivityWidgetService.checkForAnimations(this.widgetSelectionService.widgetSelection.widgets[0].animations, animLegend)) {
				this.interactivityWidgetAnimationService.addOrUpdateKeyframe(d.id, d.config.xpos + ',' + d.config.ypos, animLegend);
				return;
			}
		}
		let widgetsToDrag = this.widgetSelectionService.widgetSelection.widgets;
		// let appWidget = null;
		if (widget.fromAppId) {
			const otherAppWidgets = this.interactivityEditorService.getOtherAppWidgets(widget);
			widgetsToDrag = widgetsToDrag.concat(otherAppWidgets);
		}
		for (let i = 0, l: number = widgetsToDrag.length; i < l; i++) {
			const w: IWidget = widgetsToDrag[i];
			if (w.id !== widget.id && w instanceof StageWidget) {
				if (w.elRef !== null) {
					const el = w.elRef.nativeElement;
					if (opt === 'start') {
						this.widgetDragStartPositions[w.id] = {
							left: el.offsetLeft,
							top: el.offsetTop
						};
					} else if (opt === 'dragging') {
						const newLeft: number = this.widgetDragStartPositions[w.id].left + deltaX;
						const newTop: number = this.widgetDragStartPositions[w.id].top + deltaY;

						this.renderer.setStyle(w.elRef.nativeElement, 'left', newLeft + 'px');
						this.renderer.setStyle(w.elRef.nativeElement, 'top', newTop + 'px');
					} else if (opt === 'end') {
						if (!w.fromAppId) {
							widgetsChangeData.push({
								id: w.id,
								config: this.evtToConf({ left: el.offsetLeft, top: el.offsetTop })
							});
						}
						this.widgetDragStartPositions = {};
					}
				}
			}
		}

		if (opt === 'end') {
			this.reduxService.dispatch({
				type: TimelineDataActions.CHANGE_WIDGETS_DIFFERENT_CONFIG,
				widgets: widgetsChangeData,
				stateSet: this.interactivityEditorService.activeStateSet,
				state: this.interactivityEditorService.activeState,
				description: 'Changed position of ' + this.widgetSelectionService.widgetSelection.widgets.length + ' widget(s)'
			});
		}
	}

	private stopWidgetSelector(): void {
		if (this.dragSelecting) {	// tiny optimization
			this.dragSelecting = false;
			this.renderer.setStyle(this.widgetSelector.nativeElement, 'visibility', 'hidden');
		}
	}

	// private setGrid( x: number, y: number): void {
	// 	let gridTarget: any = (interact as any).createSnapGrid({
	// 		x: x,
	// 		y: y
	// 	});

	// 	this.snapObjectSubject.next( { targets: [gridTarget]} );
	// 	this.vGrid.drawGrid( x, y, GridType.SMALL );
	// }

	private designDimensionsChanged(designDimensions: DesignDimensions) {
		if (!this.zoomableWrapper) {
			return;
		}
		const zoomInfoPre = {
			scrollWidthPre: this.zoomableWrapper.scrollWidth,
			scrollLeftPre: this.zoomableWrapper.scrollLeft,
			scrollTopPre: this.zoomableWrapper.scrollTop
		};
		const dw = this.designWidth = designDimensions.width * designDimensions.zoomFactor;
		const dh = this.designHeight = designDimensions.height * designDimensions.zoomFactor;
		this.stageWidth = dw * (1 + this.extraCanvasAreaPercentage * 2);
		this.stageHeight = dh * (1 + this.extraCanvasAreaPercentage * 2);
		// also directly update the stageWidth otherwise we need to wait for the angular cycle to update the DOM
		this.zoomableContent.style.width = this.stageWidth + 'px';
		this.zoomableContent.style.height = this.stageHeight + 'px';
		this.mediaOffsetX = dw * this.extraCanvasAreaPercentage;
		this.mediaOffsetY = dh * this.extraCanvasAreaPercentage;

		if (this.zoomFactor && this.zoomFactor !== designDimensions.zoomFactor) {
			const lastZoomFactor = this.zoomableWrapper.scrollWidth / zoomInfoPre.scrollWidthPre;

			// offset is mouseposition at scroll or center of the canvas
			const mouseOffsetX = this.zoomMousePosition && this.zoomMousePosition.mouseOffsetX ?
				this.zoomMousePosition.mouseOffsetX : (this.zoomableWrapper.offsetWidth / 2);
			const mouseOffsetY = this.zoomMousePosition && this.zoomMousePosition.mouseOffsetY ?
				this.zoomMousePosition.mouseOffsetY : (this.zoomableWrapper.offsetHeight / 2);

			const scrollLeft = ((zoomInfoPre.scrollLeftPre + mouseOffsetX) * lastZoomFactor) - mouseOffsetX;
			const scrollTop = ((zoomInfoPre.scrollTopPre + mouseOffsetY) * lastZoomFactor) - mouseOffsetY;
			this.zoomableWrapper.scrollLeft = scrollLeft;
			this.zoomableWrapper.scrollTop = scrollTop;
			this.zoomMousePosition = {};
		}
		this.zoomFactor = designDimensions.zoomFactor;

		if (designDimensions.fromMedia) {
			// when media loaded
			this.zoomableWrapper.style.opacity = '1'; // fade in
			setTimeout(() => {
				this.zoom('toFit', null, null, true);
			});
		}
	}

	private scrollMediaInView(smooth = false) {
		const cw = this.canvasWrapper.nativeElement;
		const scrollOpts: any = {
			left: (cw.scrollWidth / 2) - (cw.offsetWidth / 2),
			top: (cw.scrollHeight / 2) - (cw.offsetHeight / 2)
		};
		if (smooth) {
			scrollOpts.behavior = 'smooth';
		}
		cw.scrollTo(scrollOpts);
	}

	private evtToConf(evt: any, includeWH = false) {
		const ret: any = {
			xpos: ((evt.left - this.mediaOffsetX) / this.designWidth) * 100 + '%',
			ypos: ((evt.top - this.mediaOffsetY) / this.designHeight) * 100 + '%'
		};
		if (includeWH) {
			ret.width = (evt.width / this.designWidth) * 100 + '%';
			ret.height = (evt.height / this.designHeight) * 100 + '%';
		}
		return ret;
	}

	private contextMenuShow(posX: number, posY: number): void {
		if (this.interactivityEditorService.parentEntityType !== 'App') {
			this.contextMenuOptions.push({
				label: `Set design dimensions (${this.designDimensions.width}x${this.designDimensions.height})`,
				value: 'setDesignDimensions',
				icon: 'square-o'
			});
		}
		this.contextMenuStyle.display = 'block';
		this.contextMenuStyle.left = posX + 'px';
		this.contextMenuStyle.top = posY + 'px';
	}

	private reCalc(): void {
		const x3 = Math.min(this.x1, this.x2); // Smaller X
		const x4 = Math.max(this.x1, this.x2); // Larger X
		const y3 = Math.min(this.y1, this.y2); // Smaller Y
		const y4 = Math.max(this.y1, this.y2); // Larger Y
		this.renderer.setStyle(this.widgetSelector.nativeElement, 'left', x3 + 'px');
		this.renderer.setStyle(this.widgetSelector.nativeElement, 'top', y3 + 'px');
		this.renderer.setStyle(this.widgetSelector.nativeElement, 'width', (x4 - x3) + 'px');
		this.renderer.setStyle(this.widgetSelector.nativeElement, 'height', (y4 - y3) + 'px');

		this.widgetSelectionService.selectInBoundingBox(x3, y3, (x4 - x3), (y4 - y3));
	}
}
